import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import Navbar from '../components/Navbar';
import { Plus, Trash2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const SmartRules = () => {
  const [rules, setRules] = useState([]);
  const [merchantKeyword, setMerchantKeyword] = useState('');
  const [category, setCategory] = useState('');
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [rulesRes, categoriesRes] = await Promise.all([
        api.get('/smart-rules'),
        api.get('/categories/expense'),
      ]);
      setRules(rulesRes.data);
      const categoryNames = categoriesRes.data.map(c => c.name).filter(n => n !== 'Uncategorized');
      setCategories(categoryNames);
      if (categoryNames.length > 0 && !category) {
        setCategory(categoryNames[0]);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load smart rules');
    } finally {
      setLoading(false);
    }
  };

  const fetchRules = async () => {
    try {
      const response = await api.get('/smart-rules');
      setRules(response.data);
    } catch (error) {
      console.error('Failed to fetch rules:', error);
    }
  };

  const handleCreateRule = async (e) => {
    e.preventDefault();

    try {
      await api.post('/smart-rules', {
        merchant_keyword: merchantKeyword,
        category,
      });

      toast.success('Smart rule created!');
      setMerchantKeyword('');
      fetchRules();
    } catch (error) {
      console.error('Failed to create rule:', error);
      toast.error('Failed to create rule');
    }
  };

  const handleDeleteRule = async (ruleId) => {
    try {
      await api.delete(`/smart-rules/${ruleId}`);
      toast.success('Rule deleted');
      fetchRules();
    } catch (error) {
      console.error('Failed to delete rule:', error);
      toast.error('Failed to delete rule');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030712] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030712]">
      <Navbar />

      <div className="pt-24 pb-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-2">
            <Sparkles className="w-8 h-8 text-indigo-400" />
            <h1 className="text-4xl md:text-5xl font-bold font-['Outfit'] tracking-tight">
              Smart Rules
            </h1>
          </div>
          <p className="text-lg text-muted-foreground">
            Auto-categorize receipts based on merchant names
          </p>
        </div>

        {/* Create Rule Form */}
        <div className="mb-8 p-6 bg-white/5 border border-white/10 rounded-2xl backdrop-blur-xl">
          <h2 className="text-xl font-semibold mb-4">Create New Rule</h2>
          <form onSubmit={handleCreateRule} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-2">
                Merchant Keyword
              </label>
              <input
                type="text"
                value={merchantKeyword}
                onChange={(e) => setMerchantKeyword(e.target.value)}
                data-testid="input-merchant-keyword"
                placeholder="e.g., Starbucks, Walmart, Shell"
                required
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <p className="text-xs text-muted-foreground mt-1">
                When a merchant name contains this keyword, it will be auto-categorized
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-2">
                Category
              </label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                data-testid="select-category"
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                {categories.map((cat) => (
                  <option key={cat} value={cat} className="bg-[#0f172a]">
                    {cat}
                  </option>
                ))}
              </select>
            </div>

            <button
              type="submit"
              data-testid="create-rule-button"
              className="w-full bg-gradient-to-r from-indigo-500 to-purple-500 text-white px-6 py-3 rounded-full font-medium hover:opacity-90 transition-opacity flex items-center justify-center space-x-2"
            >
              <Plus className="w-5 h-5" />
              <span>Create Rule</span>
            </button>
          </form>
        </div>

        {/* Rules List */}
        <div className="space-y-3">
          <h2 className="text-xl font-semibold mb-4">Your Rules</h2>
          {rules.length > 0 ? (
            rules.map((rule) => (
              <div
                key={rule.id}
                className="p-4 bg-white/5 border border-white/10 rounded-xl hover:bg-white/10 transition-colors flex items-center justify-between"
                data-testid={`rule-${rule.id}`}
              >
                <div>
                  <p className="font-medium text-lg">
                    {rule.merchant_keyword}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    → {rule.category}
                  </p>
                </div>
                <button
                  onClick={() => handleDeleteRule(rule.id)}
                  data-testid={`delete-rule-${rule.id}`}
                  className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            ))
          ) : (
            <div className="p-12 text-center text-muted-foreground bg-white/5 border border-white/10 rounded-xl">
              <Sparkles className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No smart rules yet</p>
              <p className="text-sm mt-2">Create rules to automatically categorize receipts</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SmartRules;
